import { createTheme } from '@mui/material/styles';

/**
 * 애플리케이션 전체의 Material UI 테마 설정
 * 
 * 색상, 타이포그래피, 컴포넌트 스타일 등 전역 디자인 요소를 정의합니다.
 * 일관된 UI/UX를 제공하기 위해 사용됩니다.
 * 
 * 엔터프라이즈급 전문성을 강조하는 디자인으로 구성
 */

const theme = createTheme({
  // 색상 팔레트 정의 - 전문적이고 고급스러운 색상으로 구성
  palette: {
    primary: {
      main: '#1565C0',     // 전문적인 블루 (차분하고 신뢰감 있는)
      light: '#42A5F5',    // 연한 블루
      dark: '#0D47A1',     // 진한 네이비
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#37474F',     // 차분한 블루그레이 (보조 색상)
      light: '#62727B',    // 연한 그레이
      dark: '#263238',     // 진한 그레이
      contrastText: '#ffffff',
    },
    success: {
      main: '#2E7D32',     // 차분한 녹색
      light: '#66BB6A',
      dark: '#1B5E20',
    },
    warning: {
      main: '#F57C00',     // 전문적인 오렌지
      light: '#FFB74D',
      dark: '#E65100',
    },
    error: {
      main: '#C62828',     // 차분한 빨간색
      light: '#EF5350',
      dark: '#B71C1C',
    },
    info: {
      main: '#0277BD',     // 정보 전달용 블루
      light: '#03DAC6',
      dark: '#01579B',
    },
    background: {
      default: '#FAFAFA',  // 매우 연한 회색 배경 (깔끔함)
      paper: '#FFFFFF',    // 순백색 카드 배경
    },
    text: {
      primary: '#212121',    // 진한 회색 (가독성 최적화)
      secondary: '#616161',  // 중간 회색 (부가 정보)
      disabled: '#9E9E9E',   // 비활성 텍스트
    },
    divider: '#E0E0E0',      // 구분선 색상
    
    // 커스텀 색상 추가 (엔터프라이즈용)
    neutral: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  
  // 글꼴 설정
  typography: {
    fontFamily: [
      '"NanumBarunGothic"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),  // 나눔바른고딕을 기본 폰트로 설정
    
    h1: {
      fontFamily: '"NanumBarunGothicBold", "NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01562em',
      color: '#212121',
    },
    h2: {
      fontFamily: '"NanumBarunGothicBold", "NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.00833em',
      color: '#212121',
    },
    h3: {
      fontFamily: '"NanumBarunGothicBold", "NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 600,
      letterSpacing: '0em',
      color: '#212121',
    },
    h4: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.00735em',
      color: '#212121',
    },
    h5: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 500,
      letterSpacing: '0em',
      color: '#212121',
    },
    h6: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.0075em',
      color: '#212121',
    },
    body1: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#212121',
    },
    body2: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#616161',
    },
    button: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 500,
    },
    caption: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#757575',
    },
    overline: {
      fontFamily: '"NanumBarunGothic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#757575',
    },
  },
  
  // 기본 모양 설정 - 보다 각진 형태로 전문성 강조
  shape: {
    borderRadius: 8,  // 덜 둥근 모서리로 전문적인 느낌
  },
  
  // 컴포넌트별 스타일 커스터마이징
  components: {
    // 버튼 컴포넌트 오버라이드
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',  // 버튼 텍스트 대문자 변환 비활성화
          borderRadius: '6px',    // 더 각진 형태
          fontWeight: 500,
          padding: '10px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          fontFamily: '"NanumBarunGothic", sans-serif',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        },
        contained: {
          background: '#1565C0',
          color: '#ffffff',
          '&:hover': {
            background: '#0D47A1',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#1565C0',
          color: '#1565C0',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#0D47A1',
            background: 'rgba(21, 101, 192, 0.04)',
          },
        },
        text: {
          color: '#1565C0',
          '&:hover': {
            background: 'rgba(21, 101, 192, 0.04)',
          },
        },
      },
    },
    
    // 앱바(상단 네비게이션) 컴포넌트 오버라이드
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #263238 0%, #37474F 100%)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
      },
    },
    
    // 카드 컴포넌트 오버라이드
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          borderRadius: '8px',
          border: '1px solid #E0E0E0',
          transition: 'all 0.2s ease',
          fontFamily: '"NanumBarunGothic", sans-serif',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          },
        },
      },
    },
    
    // Paper 컴포넌트 오버라이드
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
        elevation3: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
        },
      },
    },
    
    // Chip 컴포넌트 오버라이드
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
        filled: {
          backgroundColor: '#E3F2FD',
          color: '#1565C0',
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#1565C0',
          color: '#1565C0',
        },
      },
    },
    
    // Avatar 컴포넌트 오버라이드
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    
    // TextField 컴포넌트 오버라이드
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: '"NanumBarunGothic", sans-serif',
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            fontFamily: '"NanumBarunGothic", sans-serif',
            '&:hover fieldset': {
              borderColor: '#1565C0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1565C0',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"NanumBarunGothic", sans-serif',
            '&.Mui-focused': {
              color: '#1565C0',
            },
          },
        },
      },
    },
    
    // Typography 컴포넌트 오버라이드
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
      },
    },
    
    // ListItemText 컴포넌트 오버라이드
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
      },
    },
    
    // FormLabel 컴포넌트 오버라이드
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
      },
    },
    
    // Drawer 컴포넌트 오버라이드 (사이드바)
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FAFAFA',
          borderRight: '1px solid #E0E0E0',
        },
      },
    },
    
    // Menu 컴포넌트 오버라이드
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          border: '1px solid #E0E0E0',
        },
      },
    },
    
    // Table 컴포넌트 오버라이드
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
        },
      },
    },
    
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#212121',
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
        body: {
          fontFamily: '"NanumBarunGothic", sans-serif',
        },
      },
    },
  },
});

export default theme; 